import { Component, Input, forwardRef, inject, signal } from '@angular/core';
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';
import { SelectModule } from 'primeng/select';
import { LocalizationService } from '../../core/localization/localization.service';
import { KnowledgeApiService } from '../../features/knowledge/knowledge-api.service';

interface KnowledgeSourceOption {
  id: string;
  label: string;
}

@Component({
  standalone: true,
  selector: 'ain-knowledge-source-selector',
  imports: [FormsModule, SelectModule],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => KnowledgeSourceSelectorComponent),
      multi: true
    }
  ],
  template: `
    <p-select
      class="selector"
      [options]="options()"
      optionLabel="label"
      optionValue="id"
      [placeholder]="t('growth.selectors.knowledgeSource.placeholder')"
      [showClear]="showClear"
      [filter]="true"
      filterBy="label,id"
      [disabled]="disabled || loading()"
      [loading]="loading()"
      [ngModel]="value()"
      (ngModelChange)="onValueChange($event)"
      [appendTo]="appendTo" />
  `,
  styles: [`.selector { width: 100%; }`]
})
export class KnowledgeSourceSelectorComponent implements ControlValueAccessor {
  private readonly knowledgeApi = inject(KnowledgeApiService);
  private readonly localization = inject(LocalizationService);
  private onChange: (value: string | null) => void = () => {};
  private onTouched: () => void = () => {};

  @Input() showClear = true;
  @Input() appendTo: 'body' | 'self' = 'body';
  /** When set, only sources with this status are listed (e.g. Active). */
  @Input() statusFilter: string | null = null;

  readonly loading = signal(false);
  readonly options = signal<KnowledgeSourceOption[]>([]);
  readonly value = signal<string | null>(null);
  disabled = false;

  private pendingValue: string | null = null;
  private optionsLoaded = false;

  constructor() {
    this.load();
  }

  writeValue(value: string | null): void {
    this.pendingValue = value?.trim() || null;
    this.value.set(this.pendingValue);
  }

  registerOnChange(fn: (value: string | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(disabled: boolean): void {
    this.disabled = disabled;
  }

  onValueChange(value: string | null): void {
    this.value.set(value);
    this.onChange(value);
    this.onTouched();
  }

  t(key: string): string {
    return this.localization.translate(key);
  }

  reload(): void {
    this.load();
  }

  private load(): void {
    this.loading.set(true);
    this.knowledgeApi.listSources().subscribe({
      next: rows => {
        const filtered = this.statusFilter
          ? rows.filter(row => row.status === this.statusFilter)
          : rows;
        this.options.set(filtered.map(row => ({
          id: row.id,
          label: `${row.sourceType} (${row.status})`
        })));
        this.optionsLoaded = true;
        if (this.pendingValue) {
          this.value.set(this.pendingValue);
        }
        this.loading.set(false);
      },
      error: () => {
        this.options.set([]);
        this.optionsLoaded = true;
        this.loading.set(false);
      }
    });
  }
}
